<template>
    <section
        class="ContentCarousel"
        :class="{ 'ContentCarousel--with-theme': themeBaseColour }"
        v-show="calculatedCarouselItems.length"
    >
        <div class="ContentCarousel__wrapper">
            <Splide
                class="ContentCarousel__splide"
                :has-track="false"
                :options="{
                    arrows: true,
                    gap: '1.25rem',
                    perPage: 1,
                    padding: { right: '10%' },
                    mediaQuery: 'min',
                    perMove: 1,
                    breakpoints: {
                        768: {
                            perPage: 2,
                            padding: { right: '15%' },
                        },
                        1280: {
                            perPage: 3,
                            padding: { right: '15%' },
                        },
                        1920: {
                            perPage: 4,
                            gap: '1.5rem',
                            padding: { right: '15%' },
                        },
                    },
                }"
            >
                <div class="ContentCarousel__title-wrapper">
                    <div
                        v-if="
                            slice.primary.section_title.length ||
                            slice.primary.section_subtitle.length
                        "
                        class="ContentCarousel__title-section"
                    >
                        <PrismicRichText
                            v-if="slice.primary.section_title.length"
                            :field="slice.primary.section_title"
                            :id="sliceId"
                            class="ContentCarousel__title"
                        />
                        <PrismicRichText
                            v-if="slice.primary.section_subtitle.length"
                            :field="slice.primary.section_subtitle"
                            class="ContentCarousel__desc"
                        />
                    </div>
                    <div class="ContentCarousel__arrow-wrapper splide__arrows">
                        <button
                            class="ContentCarousel__arrow splide__arrow splide__arrow--prev"
                            @mouseover="toggleCursorOn"
                            @mouseleave="toggleCursorOff"
                        >
                            <FontIcon
                                icon="arrow"
                                color="lime-green"
                                size="stretch"
                                :rotate="225"
                                class="ContentCarousel__arrow-icon"
                            />
                        </button>
                        <button
                            class="ContentCarousel__arrow splide__arrow splide__arrow--next"
                            @mouseover="toggleCursorOn"
                            @mouseleave="toggleCursorOff"
                        >
                            <FontIcon
                                icon="arrow"
                                color="lime-green"
                                size="stretch"
                                :rotate="45"
                                class="ContentCarousel__arrow-icon"
                            />
                        </button>
                    </div>
                </div>
                <SplideTrack>
                    <template
                        v-for="(rowItem, rowIndex) in calculatedCarouselItems"
                        :key="rowIndex"
                    >
                        <SplideSlide>
                            <ImageCard
                                :item="rowItem"
                                :theme="rowItem.theme"
                                :small-text="true"
                            />
                        </SplideSlide>
                    </template>
                </SplideTrack>
            </Splide>
        </div>
        <div class="ContentCarousel__button-container">
            <CustomButton
                v-if="
                    slice.primary.section_button.text &&
                    slice.primary.section_button.url
                "
                :to="slice.primary.section_button.url"
                :style="buttonStyles"
                class="ContentCarousel__button"
            >
                {{ slice.primary.section_button.text }}
            </CustomButton>
        </div>
    </section>
</template>

<script setup lang="ts">
import { type Content } from '@prismicio/client'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

// The array passed to `getSliceComponentProps` is purely optional.
// Consider it as a visual hint for you when templating your slice.
const props = defineProps(
    getSliceComponentProps<Content.ContentCarouselSlice>([
        'slice',
        'index',
        'slices',
        'context',
    ]),
)

const prismic = usePrismic()

const cursorStore = useCursorStore()

function toggleCursorOn() {
    cursorStore.setCursorType('link-hover')
}

function toggleCursorOff() {
    cursorStore.setCursorType('default')
}

const newsPostIds = computed(() => {
    return props.slice.primary.carousel_items
        .filter((carouselItem) => carouselItem.linked_content.data)
        .map((carouselItem) => carouselItem.linked_content.id)
})

const sliceId = props.slice.id.replace('$', '-') + '-content-carousel'

const { data: newsPosts } = await useAsyncData(
    `[news-posts-for-content-carousel-${props.slice.id.replace('$', '-')}]`,
    () =>
        prismic.client.get({
            filters: [
                prismic.filter.at('document.type', 'new_post'),
                prismic.filter.in('document.id', newsPostIds.value),
            ],
            fetchLinks: ['author.name'],
            pageSize: 100,
        }),
)

const buttonStyles = computed(() => {
    if (props.slice.primary.section_theme?.data) {
        return {
            '--custom-button-override-colour':
                props.slice.primary.section_theme?.data?.theme_base_colour,
            '--custom-button-text-override-colour':
                props.slice.primary.section_theme?.data?.theme_text_colour ||
                'white',
        }
    }

    return {}
})

const themeBaseColour = ref(
    props.slice.primary.section_theme?.data?.theme_base_colour,
)

const themeBaseIconColour = ref(
    props.slice.primary.section_theme?.data?.theme_base_icon_colour || '#fff',
)

const calculatedCarouselItems = computed(() => {
    return newsPosts.value.results
        .filter((carouselItem) => carouselItem.data)
        .map((carouselItem) => {
            if (carouselItem.type === 'new_post') {
                return {
                    image: {
                        path: carouselItem.data.image?.url,
                        dimensions: {
                            width:
                                carouselItem.data.image?.dimensions?.width / 2,
                            height:
                                carouselItem.data.image?.dimensions?.height / 2,
                        },
                    },
                    title: carouselItem.data.title,
                    tags:
                        carouselItem.data.tags
                            ?.map((tag) => tag.tag_text)
                            ?.filter(Boolean) || [],
                    to: carouselItem.url,
                    listInfo: [
                        {
                            text: Intl.DateTimeFormat('en-GB', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                            }).format(
                                new Date(
                                    carouselItem.data.original_publish_date ??
                                        carouselItem.first_publication_date,
                                ),
                            ),
                        },
                        ...(carouselItem.data.author?.data?.name
                            ? [
                                  {
                                      text: carouselItem.data.author.data?.name,
                                  },
                              ]
                            : [null]),
                    ].filter((a) => a),
                    cursorText: 'View',
                    theme: {
                        ...(props.slice.primary.section_theme || {}),
                        hideThemeLogo: true,
                    },
                }
            }
        })
        .filter(Boolean)
})
</script>

<style lang="scss">
.TextHero + .ContentCarousel {
    margin-top: 4rem;

    @include medium-up {
        margin-top: 4rem;
    }
}

.ContentCarousel {
    column-gap: var(--grid-col-gap);
    display: grid;
    grid-column: 1 / span 24;
    grid-template-columns: repeat(24, 1fr);
    overflow: hidden;
    margin-top: 5.5rem;

    @include medium-up {
        margin-top: 6.25rem;
    }
}

.ContentCarousel--with-theme {
    .ContentCarousel__arrow-icon {
        --font-icon-colour: v-bind(themeBaseColour);
    }

    .ContentCarousel__arrow {
        border: 1px solid v-bind(themeBaseColour);

        &:hover:not(:disabled) {
            background-color: v-bind(themeBaseColour);

            .ContentCarousel__arrow-icon {
                --font-icon-colour: v-bind(themeBaseIconColour);
            }
        }
    }
}

.ContentCarousel__title-wrapper {
    grid-column: 2 / span 22;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 2rem;
    margin-bottom: 2.5rem;
}

.ContentCarousel__arrow {
    border: 1px solid var(--lime-green);
    padding: 0.94rem;

    &:disabled {
        opacity: 0.6;
    }

    &:hover:not(:disabled) {
        background-color: var(--lime-green);

        .ContentCarousel__arrow-icon {
            --font-icon-colour: var(--white);
        }
    }
}

.ContentCarousel__arrow-wrapper {
    display: none;

    @include medium-up {
        display: flex;
        gap: 1rem;
    }
}

.ContentCarousel__arrow-icon {
    width: 1rem;
    height: 1rem;
}

.ContentCarousel__title {
    &:has(+ .ContentCarousel__desc) {
        margin-bottom: 1.5rem;
    }

    h2 {
        @include heading-2;
    }
}

.ContentCarousel__desc {
    @include medium-up {
        @include body-lg;

        text-wrap: balance;
    }
}

.ContentCarousel__title,
.ContentCarousel__desc {
    max-width: 60rem;
}

.ContentCarousel__wrapper {
    grid-column: 2 / span 22;
    overflow: visible;
}

.ContentCarousel__splide {
    .splide__track {
        overflow: visible;
    }
}

.ContentCarousel__button-container {
    grid-column: 2 / span 22;
    margin-top: 2rem;

    @include medium-up {
        margin-top: 2.5rem;
    }
}
</style>
